import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useAppContext } from 'providers/AppProvider';
import { Button, Col, Form, ListGroup, Modal, Row } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import { sendCustomJobEmail } from 'nursd-dashboard/api/webNotification';
import { useAuth } from 'nursd-dashboard/context/AuthContext';
import { toast } from 'react-toastify';
import { updateJobTable } from 'nursd-dashboard/api/jobTable';
import Flex from 'components/common/Flex';

const ShareEventModal = ({ setShowShare, showShare, data, agency }) => {
  const {
    config: { isDark }
  } = useAppContext();

  const { authToken } = useAuth();

  const [shareType, setShareType] = useState('all');
  const date = new Date();
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(date.setDate(date.getDate() + 7));
  const [selectedCoordinators, setSelectedCoordinators] = useState([]);

  const handleClose = () => {
    setShowShare(!showShare);
    setShareType('all');
    setStartDate(new Date());
    setEndDate(date.setDate(date.getDate() + 7));
    setSelectedCoordinators([]);
  };

  const onChange = dates => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const handleNextWeek = () => {
    const today = new Date();
    const nextSunday = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() - today.getDay() + 7
    );
    const nextSaturday = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() - today.getDay() + 13
    );

    setStartDate(nextSunday);
    setEndDate(nextSaturday);
  };

  const handleNextMonth = () => {
    const today = new Date();
    const startOfNextMonth = new Date(
      today.getFullYear(),
      today.getMonth() + 1,
      1
    );
    const endOfNextMonth = new Date(
      today.getFullYear(),
      today.getMonth() + 2,
      0
    );

    setStartDate(startOfNextMonth);
    setEndDate(endOfNextMonth);
  };

  const handleThisMonth = () => {
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    setStartDate(startOfMonth);
    setEndDate(endOfMonth);
  };

  let openJobData =
    shareType === 'range'
      ? data?.filter(
          i =>
            moment(i?.start).isAfter(startDate) &&
            moment(i.end).isBefore(endDate)
        )
      : data;

  const createTableHtml = data => {
    let html =
      '<table border="1"><thead><tr><th>Customer</th><th>Address</th><th>Start Time</th><th>End Time</th></tr></thead><tbody>';

    data.forEach(row => {
      html += `<tr><td>${row?.original?.customerName}</td><td>${
        row?.original?.fullAddress
      }</td><td>${moment(row?.start).format(
        'MM/DD/YYYY hh:mm'
      )}</td><td>${moment(row?.end).format('MM/DD/YYYY hh:mm')}</td></tr>`;
    });

    html += '</tbody></table>';
    return html;
  };

  const handleJobShare = () => {
    if (selectedCoordinators.length === 0) {
      toast.warning('Select coordinator(s) to share the job', {
        theme: 'colored'
      });
    } else {
      let data = openJobData?.map(i => i?.original);

      const postJobPromise = jobData => {
        const jobSharedIds = selectedCoordinators.map(coordinator => ({
          name: coordinator?.original?.additionalFields[0]?.agencyName,
          type: 'Agency',
          id: Number(coordinator?.id)
        }));
        const jobSharedActions = selectedCoordinators.map(coordinator => ({
          name: coordinator?.original?.additionalFields[0]?.agencyName,
          type: 'Waiting',
          id: Number(coordinator?.id)
        }));
        // console.log({
        //   jobSharedType: ['Agency'],
        //   jobSharedId: jobSharedIds,
        //   jobSharedAction: 'Waiting'
        // });
        // eslint-disable-next-line no-async-promise-executor
        return new Promise(async (resolve, reject) => {
          await updateJobTable(authToken, jobData?.id, {
            jobSharedType: ['Agency'],
            jobSharedId: jobSharedIds,
            jobSharedAction: jobSharedActions
          })
            .then(res => {
              console.log(res);
              resolve(res); // Resolve the promise with the response
            })
            .catch(error => {
              console.error('error', error);
              reject(error); // Reject the promise with the error
            });
        });
      };

      Promise.all(data?.map(job => postJobPromise(job)))
        .then(() => {
          // If any invalid rows to show again
          handleShare();
        })
        .catch(error => {
          console.error('error', error);
        });
    }
  };

  const handleShare = () => {
    const tableHtmlString = createTableHtml(openJobData);
    selectedCoordinators.forEach(coordinator => {
      // console.log(
      //   coordinator?.emailId,
      //   coordinator?.original?.additionalFields[0]?.agencyName
      // );
      sendCustomJobEmail(authToken, {
        email: coordinator?.emailId,
        name: coordinator?.original?.additionalFields[0]?.agencyName,
        subject: 'Job Alert',
        content: '',
        body: tableHtmlString
      })
        .then(() => {
          toast.success(`Job shared successfully with ${coordinator?.name}.`, {
            theme: 'colored'
          });
        })
        .catch(e => console.log(e));
    });
    toast.success(`Job shared successfully`, {
      theme: 'colored'
    });
    handleClose();
  };

  const handleCoordinatorSelection = coordinator => {
    if (selectedCoordinators.includes(coordinator)) {
      setSelectedCoordinators(prev =>
        prev.filter(item => item.id !== coordinator.id)
      );
    } else {
      setSelectedCoordinators(prev => [...prev, coordinator]);
    }
  };

  return (
    <Modal
      show={showShare}
      onHide={handleClose}
      contentClassName="border"
      backdrop="static"
      size="lg"
    >
      <Modal.Header
        closeButton
        closeVariant={isDark ? 'white' : undefined}
        className="bg-body-tertiary px-x1 border-bottom-0"
      >
        <Modal.Title id="example-modal-sizes-title-lg">
          Share Open Shift
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-x1 pb-x1 pt-1 fs-10">
        <h4 className="mb-3 fs-9">Which open shift do you want to share?</h4>
        <Form.Group className="mb-1" controlId="allShift">
          <Form.Check
            inline
            type="radio"
            name="shareType"
            id="allShift"
            className="me-2"
            checked={shareType === 'all' ? true : false}
            onChange={() => setShareType('all')}
          />
          <Form.Label className="mb-0">
            All open shifts ({openJobData?.length} shifts selected)
          </Form.Label>
        </Form.Group>
        <Form.Group className="mb-3" controlId="dateRange">
          <Form.Check
            inline
            type="radio"
            name="shareType"
            id="dateRange"
            className="me-2 mt-1"
            checked={shareType === 'range' ? true : false}
            onChange={() => setShareType('range')}
          />
          <Form.Label className="mb-0">
            Open shifts from a select date range
          </Form.Label>
        </Form.Group>
        {shareType === 'range' && (
          <Row>
            <Col>
              <DatePicker
                popperPlacement="bottom-start"
                selected={startDate}
                onChange={onChange}
                startDate={startDate}
                formatWeekDay={day => day.slice(0, 3)}
                calendarContainer={({ children }) => (
                  <div className="react-datepicker react-datepicker__predefined-ranges">
                    <ListGroup
                      variant="flush"
                      className="react-datepicker__ranges"
                    >
                      <ListGroup.Item action onClick={handleThisMonth}>
                        This Month
                      </ListGroup.Item>
                      <ListGroup.Item action onClick={handleNextWeek}>
                        Next Week
                      </ListGroup.Item>
                      <ListGroup.Item action onClick={handleNextMonth}>
                        Next Month
                      </ListGroup.Item>
                    </ListGroup>
                    {children}
                  </div>
                )}
                showPreviousMonths
                endDate={endDate}
                selectsRange
                dateFormat="MM/dd/YYYY"
                className="form-control"
              />
            </Col>
            <Col className="mt-2">
              <h4 className="mb-3 fs-9">
                ({openJobData?.length} shifts selected)
              </h4>
            </Col>
          </Row>
        )}
        <h4 className="my-3 fs-9">Agency Provider (Marketplace)</h4>
        <p className="my-3 fs-9">No Data Available</p>
        <Flex justifyContent={'between'}>
          <h4 className="my-3 fs-9">Agency coordinators</h4>{' '}
        </Flex>
        <Flex>
          <Form.Check
            type="checkbox"
            id={`checkbox`}
            label=" "
            checked={agency?.length === selectedCoordinators?.length}
            onChange={() => {
              agency?.length === selectedCoordinators?.length
                ? setSelectedCoordinators([])
                : setSelectedCoordinators(agency);
            }}
          />

          <h4 className="fs-9 mx-3">All</h4>
        </Flex>

        {agency?.map((item, index) => {
          const isSelected = selectedCoordinators.some(
            coordinator => coordinator.id === item.id
          );

          return (
            <Row key={index} className="mt-3">
              <Col>
                <Row>
                  <Col xs={1}>
                    <Form.Check
                      type="checkbox"
                      id={`checkbox-${item.id}`}
                      label=" "
                      checked={isSelected}
                      onChange={() => {
                        handleCoordinatorSelection(item);
                      }}
                    />
                  </Col>
                  <Col xs={11}>
                    <h4 className="fs-9">{item?.name}</h4>
                  </Col>
                </Row>
              </Col>
              <Col>
                <Row>
                  {item?.licenseTypes?.map((license, licenseIndex) => {
                    return (
                      <Col xs={2} key={licenseIndex} className="fs-10">
                        {license?.label}
                      </Col>
                    );
                  })}
                </Row>
              </Col>
            </Row>
          );
        })}
      </Modal.Body>
      <Modal.Footer className="bg-body-tertiary px-x1 border-top-0">
        <Button
          variant={openJobData?.length !== 0 ? 'primary' : 'secondary'}
          onClick={() => {
            openJobData?.length !== 0 && handleJobShare();
          }}
        >
          Share
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

ShareEventModal.propTypes = {
  setShowShare: PropTypes.func.isRequired,
  showShare: PropTypes.bool.isRequired,
  data: PropTypes.array.isRequired,
  agency: PropTypes.array.isRequired,
  facility: PropTypes.array.isRequired
};

export default ShareEventModal;
